import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../components/ui/alert';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  FileBox,
  Upload,
  Download,
  Trash2,
  FileText,
  File,
  Loader2,
  Search,
  FolderArchive,
  Plus,
  FileUp,
  CheckCircle,
  XCircle,
  SkipForward,
  AlertTriangle,
  Eye,
  X,
  Info,
  List,
  FolderUp,
  Unlink,
  Link,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Desenhos() {
  const { canCreate } = useAuth();
  const fileInputRef = useRef(null);
  const bulkFileInputRef = useRef(null);
  
  // List state
  const [drawings, setDrawings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  
  // Search/filter state
  const [searchFilter, setSearchFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(0);
  const [totalDrawings, setTotalDrawings] = useState(0);
  const PAGE_SIZE = 50;
  const searchInputRef = useRef(null);
  
  // Unlinked files state
  const [unlinkedFiles, setUnlinkedFiles] = useState([]);
  const [unlinkedTotal, setUnlinkedTotal] = useState(0);
  const [unlinkedSearch, setUnlinkedSearch] = useState('');
  const [unlinkedPage, setUnlinkedPage] = useState(0);
  const [loadingUnlinked, setLoadingUnlinked] = useState(false);
  
  // Dialogs
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedDrawing, setSelectedDrawing] = useState(null);
  
  // PDF Preview state
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewDrawing, setPreviewDrawing] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Single upload form
  const [uploadForm, setUploadForm] = useState({
    code_id: '',
    code: '',
    revision: 0,
    files: []
  });
  const [codeSearch, setCodeSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Bulk upload state
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  
  // Revision dialog state
  const [showRevisionDialog, setShowRevisionDialog] = useState(false);
  const [revisionDrawing, setRevisionDrawing] = useState(null);
  const [downloadingRevision, setDownloadingRevision] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);
  const [bulkResults, setBulkResults] = useState(null);
  
  // Batch download state
  const [batchFile, setBatchFile] = useState(null);
  const [batchAnalysis, setBatchAnalysis] = useState(null);
  const [analyzingBatch, setAnalyzingBatch] = useState(false);
  const [downloadingBatch, setDownloadingBatch] = useState(false);
  const batchFileInputRef = useRef(null);
  const [batchFileType, setBatchFileType] = useState('all'); // all, pdf, dxf
  const [batchRevisionChoice, setBatchRevisionChoice] = useState('requested'); // requested, latest
  const [showRevisionChoiceDialog, setShowRevisionChoiceDialog] = useState(false);
  const [batchStatusFilter, setBatchStatusFilter] = useState('all'); // all, ok, outdated, not_found
  
  // Smart upload state
  const [codesToCreate, setCodesToCreate] = useState([]);
  const [uploadingConfirm, setUploadingConfirm] = useState(false);

  // Unlinked file preview state
  const [showUnlinkedPreview, setShowUnlinkedPreview] = useState(false);
  const [unlinkedPreviewFile, setUnlinkedPreviewFile] = useState(null);
  const [unlinkedPreviewUrl, setUnlinkedPreviewUrl] = useState(null);
  const [unlinkedPreviewLoading, setUnlinkedPreviewLoading] = useState(false);

  useEffect(() => {
    fetchData();
    fetchDrawings();
  }, []);

  useEffect(() => {
    fetchDrawings();
  }, [typeFilter, page]);

  const fetchData = async () => {
    try {
      const statsRes = await axios.get(`${API_URL}/api/drawings/stats`);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
    // Note: fetchDrawings() is not called here because the useEffect hook
    // already handles fetching drawings when searchFilter, typeFilter, or page changes.
    // This prevents race conditions with the search functionality.
  };

  const fetchDrawings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', PAGE_SIZE);
      params.append('skip', page * PAGE_SIZE);
      if (searchFilter) params.append('search', searchFilter);
      if (typeFilter) params.append('file_type', typeFilter);
      
      const response = await axios.get(`${API_URL}/api/drawings/list?${params.toString()}`);
      setDrawings(response.data.drawings || []);
      setTotalDrawings(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch drawings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnlinkedFiles = async () => {
    setLoadingUnlinked(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', PAGE_SIZE);
      params.append('skip', unlinkedPage * PAGE_SIZE);
      if (unlinkedSearch) params.append('search', unlinkedSearch);
      
      const response = await axios.get(`${API_URL}/api/drawings/unlinked?${params.toString()}`);
      setUnlinkedFiles(response.data.files || []);
      setUnlinkedTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch unlinked files:', error);
    } finally {
      setLoadingUnlinked(false);
    }
  };

  // Single upload functions
  const searchCodes = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/codes?search=${encodeURIComponent(query)}&page_size=10`);
      setSearchResults(response.data.items || []);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleCodeSearchChange = (value) => {
    setCodeSearch(value);
    const timer = setTimeout(() => searchCodes(value), 300);
    return () => clearTimeout(timer);
  };

  const selectCode = (code) => {
    setUploadForm({
      ...uploadForm,
      code_id: code.id,
      code: code.code,
      revision: code.revision
    });
    setCodeSearch(code.code);
    setSearchResults([]);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(f => 
      f.name.toLowerCase().endsWith('.pdf') || f.name.toLowerCase().endsWith('.dxf')
    );
    if (validFiles.length !== files.length) {
      toast.warning('Apenas arquivos PDF e DXF são aceitos');
    }
    setUploadForm({ ...uploadForm, files: validFiles });
  };

  const handleUpload = async () => {
    if (!uploadForm.code_id || uploadForm.files.length === 0) {
      toast.error('Selecione um código e pelo menos um arquivo');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('revision', uploadForm.revision);
      uploadForm.files.forEach(file => {
        formData.append('files', file);
      });

      const response = await axios.post(
        `${API_URL}/api/drawings/upload/${uploadForm.code_id}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      toast.success(response.data.message);
      setShowUploadDialog(false);
      setUploadForm({ code_id: '', code: '', revision: 0, files: [] });
      setCodeSearch('');
      fetchDrawings();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar arquivos');
    } finally {
      setUploading(false);
    }
  };

  // Download and delete
  const handleDownload = async (drawing) => {
    // Check if drawing has multiple revisions
    if (drawing.revision_count && drawing.revision_count > 1) {
      setRevisionDrawing(drawing);
      setShowRevisionDialog(true);
      return;
    }
    
    // Direct download if only one revision
    await downloadFile(drawing.id, drawing.original_filename);
  };

  const downloadFile = async (drawingId, filename, revision = null) => {
    try {
      const url = revision 
        ? `${API_URL}/api/drawings/download-revision/${drawingId}/${revision}`
        : `${API_URL}/api/drawings/download/${drawingId}`;
      
      const response = await axios.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Download iniciado');
    } catch (error) {
      toast.error('Erro ao baixar arquivo');
    }
  };

  const handleDownloadRevision = async (revision) => {
    if (!revisionDrawing) return;
    setDownloadingRevision(true);
    await downloadFile(revisionDrawing.id, revision.filename, revision.revision);
    setDownloadingRevision(false);
  };

  // View specific revision PDF
  const handleViewRevision = async (revision) => {
    if (!revisionDrawing) return;
    
    // Only allow PDF viewing
    const fileType = revision.file_type || revisionDrawing.file_type || 'pdf';
    if (fileType.toUpperCase() !== 'PDF') {
      toast.error('Apenas arquivos PDF podem ser visualizados');
      return;
    }
    
    try {
      setPreviewLoading(true);
      const response = await axios.get(
        `${API_URL}/api/drawings/view-revision/${revisionDrawing.id}/${revision.revision}`,
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Build the correct filename for this revision
      const revisionFilename = `${revisionDrawing.base_code}-${revision.revision}.pdf`;
      const revisionCode = `${revisionDrawing.base_code}-${revision.revision}`;
      
      // Close revision dialog and open preview
      setShowRevisionDialog(false);
      setPreviewDrawing({ 
        ...revisionDrawing, 
        blobUrl,
        revision: revision.revision,
        filename: revision.filename,
        original_filename: revisionFilename,
        code: revisionCode
      });
      setShowPreviewDialog(true);
    } catch (error) {
      console.error('Error viewing revision:', error);
      toast.error('Erro ao carregar PDF');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Batch download functions
  const handleBatchFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Selecione um arquivo Excel (.xlsx ou .xls)');
      return;
    }
    
    setBatchFile(file);
    setBatchAnalysis(null);
    setBatchStatusFilter('all'); // Reset filter when new file is uploaded
    
    // Analyze the file
    setAnalyzingBatch(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API_URL}/api/drawings/batch-analyze`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setBatchAnalysis(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao analisar arquivo');
      setBatchFile(null);
    } finally {
      setAnalyzingBatch(false);
    }
  };

  const handleBatchDownloadClick = () => {
    // If there are outdated items, show choice dialog
    if (batchAnalysis && batchAnalysis.outdated > 0) {
      setShowRevisionChoiceDialog(true);
    } else {
      // No outdated, proceed directly
      handleBatchDownload('requested');
    }
  };

  const handleBatchDownload = async (revisionChoice) => {
    if (!batchFile) return;
    
    setShowRevisionChoiceDialog(false);
    setDownloadingBatch(true);
    try {
      const formData = new FormData();
      formData.append('file', batchFile);
      formData.append('file_type', batchFileType);
      formData.append('revision_preference', revisionChoice); // 'requested' or 'latest'
      
      const response = await axios.post(
        `${API_URL}/api/drawings/batch-download`,
        formData,
        { 
          headers: { 'Content-Type': 'multipart/form-data' },
          responseType: 'blob'
        }
      );
      
      // Download the ZIP
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const typeLabel = batchFileType === 'all' ? '' : `_${batchFileType}`;
      link.setAttribute('download', `desenhos${typeLabel}_${new Date().toISOString().slice(0, 10)}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Download iniciado! Verifique o arquivo _RELATORIO.txt no ZIP.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao baixar arquivos');
    } finally {
      setDownloadingBatch(false);
    }
  };

  const clearBatchSelection = () => {
    setBatchFile(null);
    setBatchAnalysis(null);
    setBatchFileType('all');
    setBatchRevisionChoice('requested');
    if (batchFileInputRef.current) {
      batchFileInputRef.current.value = '';
    }
  };

  const handleFileTypeChange = async (newType) => {
    setBatchFileType(newType);
    
    // Re-analyze with new filter if file is already selected
    if (batchFile) {
      setAnalyzingBatch(true);
      try {
        const formData = new FormData();
        formData.append('file', batchFile);
        formData.append('file_type', newType);
        
        const response = await axios.post(
          `${API_URL}/api/drawings/batch-analyze`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        
        setBatchAnalysis(response.data);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erro ao analisar arquivo');
      } finally {
        setAnalyzingBatch(false);
      }
    }
  };

  // Preview PDF
  const handlePreview = async (drawing) => {
    if (drawing.file_type?.toUpperCase() !== 'PDF') {
      toast.error('Apenas arquivos PDF podem ser visualizados');
      return;
    }
    
    // Fetch the PDF and open in a new window with blob URL
    try {
      setPreviewLoading(true);
      const response = await axios.get(
        `${API_URL}/api/drawings/view/${drawing.id}`,
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Open in dialog with blob URL
      setPreviewDrawing({ ...drawing, blobUrl });
      setShowPreviewDialog(true);
    } catch (error) {
      toast.error('Erro ao carregar PDF');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewDrawing?.blobUrl) {
      window.URL.revokeObjectURL(previewDrawing.blobUrl);
    }
    setPreviewDrawing(null);
    setShowPreviewDialog(false);
  };

  // Preview unlinked PDF files
  const handlePreviewUnlinked = async (file) => {
    if (file.file_type !== 'PDF') {
      toast.error('Apenas arquivos PDF podem ser visualizados');
      return;
    }
    
    setUnlinkedPreviewFile(file);
    setUnlinkedPreviewLoading(true);
    setShowUnlinkedPreview(true);
    
    try {
      const response = await axios.get(
        `${API_URL}/api/drawings/view-unlinked/${encodeURIComponent(file.filename)}`,
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      setUnlinkedPreviewUrl(blobUrl);
    } catch (error) {
      toast.error('Erro ao carregar PDF');
      setShowUnlinkedPreview(false);
    } finally {
      setUnlinkedPreviewLoading(false);
    }
  };

  const closeUnlinkedPreview = () => {
    if (unlinkedPreviewUrl) {
      window.URL.revokeObjectURL(unlinkedPreviewUrl);
    }
    setUnlinkedPreviewUrl(null);
    setUnlinkedPreviewFile(null);
    setShowUnlinkedPreview(false);
  };

  const handleDelete = async () => {
    if (!selectedDrawing) return;
    try {
      await axios.delete(`${API_URL}/api/drawings/${selectedDrawing.id}`);
      toast.success('Arquivo excluído com sucesso');
      setShowDeleteDialog(false);
      setSelectedDrawing(null);
      fetchDrawings();
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir arquivo');
    }
  };

  // Bulk upload functions
  const handleBulkFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    const validFiles = selectedFiles.filter(f => {
      const ext = f.name.split('.').pop().toLowerCase();
      return ['pdf', 'dxf'].includes(ext);
    });
    setBulkFiles(validFiles);
    setBulkResults(null);
  };

  const handleBulkDrop = (event) => {
    event.preventDefault();
    const droppedFiles = Array.from(event.dataTransfer.files || []);
    const validFiles = droppedFiles.filter(f => {
      const ext = f.name.split('.').pop().toLowerCase();
      return ['pdf', 'dxf'].includes(ext);
    });
    setBulkFiles(prev => [...prev, ...validFiles]);
    setBulkResults(null);
  };

  const handleBulkDragOver = (event) => {
    event.preventDefault();
  };

  const removeBulkFile = (index) => {
    setBulkFiles(prev => prev.filter((_, i) => i !== index));
  };

  const clearBulkFiles = () => {
    setBulkFiles([]);
    setBulkResults(null);
    setCodesToCreate([]);
    if (bulkFileInputRef.current) {
      bulkFileInputRef.current.value = '';
    }
  };

  // Smart upload - analyze files
  const handleSmartAnalyze = async () => {
    if (bulkFiles.length === 0) {
      toast.error('Selecione pelo menos um arquivo');
      return;
    }
    
    setBulkUploading(true);
    setBulkResults(null);
    setCodesToCreate([]);
    
    try {
      const formData = new FormData();
      bulkFiles.forEach(file => formData.append('files', file));
      
      const response = await axios.post(
        `${API_URL}/api/drawings/smart-upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setBulkResults(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao analisar arquivos');
    } finally {
      setBulkUploading(false);
    }
  };

  // Smart upload - confirm and execute
  const handleSmartUploadConfirm = async () => {
    if (bulkFiles.length === 0) return;
    
    setUploadingConfirm(true);
    
    try {
      const formData = new FormData();
      bulkFiles.forEach(file => formData.append('files', file));
      formData.append('create_codes', codesToCreate.join(','));
      
      const response = await axios.post(
        `${API_URL}/api/drawings/smart-upload/confirm`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success(response.data.message);
      
      if (response.data.created_codes?.length > 0) {
        toast.info(`${response.data.created_codes.length} código(s) criado(s) no catálogo`);
      }
      
      if (response.data.structures_updated > 0) {
        toast.info(`${response.data.structures_updated} estrutura(s) atualizada(s) com nova revisão`);
      }
      
      clearBulkFiles();
      fetchDrawings();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar arquivos');
    } finally {
      setUploadingConfirm(false);
    }
  };

  const handleBulkUpload = async () => {
    if (bulkFiles.length === 0) {
      toast.error('Selecione pelo menos um arquivo');
      return;
    }

    setBulkUploading(true);
    setBulkProgress(0);
    setBulkResults(null);

    const formData = new FormData();
    bulkFiles.forEach(file => {
      formData.append('files', file);
    });

    try {
      const response = await axios.post(`${API_URL}/api/drawings/bulk-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setBulkProgress(percent);
        }
      });

      setBulkResults(response.data);
      
      if (response.data.linked > 0) {
        toast.success(`${response.data.linked} desenho(s) vinculado(s) com sucesso!`);
        fetchDrawings();
        fetchData();
      }
      
      if (response.data.not_found > 0) {
        toast.warning(`${response.data.not_found} arquivo(s) não encontraram código correspondente`);
      }

      if (response.data.linked > 0) {
        setBulkFiles([]);
        if (bulkFileInputRef.current) {
          bulkFileInputRef.current.value = '';
        }
      }

    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar arquivos');
    } finally {
      setBulkUploading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'linked': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'not_found': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'skipped': return <SkipForward className="w-4 h-4 text-yellow-500" />;
      case 'error': return <AlertTriangle className="w-4 h-4 text-red-500" />;
      default: return null;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'linked': return <Badge className="bg-green-100 text-green-700">Vinculado</Badge>;
      case 'not_found': return <Badge className="bg-red-100 text-red-700">Não Encontrado</Badge>;
      case 'skipped': return <Badge className="bg-yellow-100 text-yellow-700">Ignorado</Badge>;
      case 'error': return <Badge className="bg-red-100 text-red-700">Erro</Badge>;
      default: return null;
    }
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-7xl" data-testid="desenhos-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
              <FileBox className="w-7 h-7 text-blue-600" strokeWidth={1.5} />
              Desenhos
            </h1>
            <p className="text-slate-500 mt-1">
              Gerencie arquivos PDF e DXF vinculados aos códigos
            </p>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-4">
                <p className="text-2xl font-bold text-slate-900">{stats.total_drawings}</p>
                <p className="text-sm text-slate-500">Total de Desenhos</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-4">
                <p className="text-2xl font-bold text-blue-600">{stats.pdf_count || 0}</p>
                <p className="text-sm text-slate-500">Arquivos PDF</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-4">
                <p className="text-2xl font-bold text-green-600">{stats.dxf_count || 0}</p>
                <p className="text-sm text-slate-500">Arquivos DXF</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-4">
                <p className="text-2xl font-bold text-purple-600">{stats.codes_with_drawings || 0}</p>
                <p className="text-sm text-slate-500">Códigos com Desenhos</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs: List and Bulk Upload */}
        <Tabs defaultValue="list" className="w-full" onValueChange={(val) => {
          if (val === 'unlinked') fetchUnlinkedFiles();
        }}>
          <TabsList className="grid w-full max-w-2xl grid-cols-4">
            <TabsTrigger value="list" className="flex items-center gap-2">
              <List className="w-4 h-4" />
              Vinculados
            </TabsTrigger>
            <TabsTrigger value="unlinked" className="flex items-center gap-2">
              <Unlink className="w-4 h-4" />
              Não Vinculados
            </TabsTrigger>
            <TabsTrigger value="batch-download" className="flex items-center gap-2">
              <Download className="w-4 h-4" />
              Download em Massa
            </TabsTrigger>
            {canCreate() && (
              <TabsTrigger value="bulk" className="flex items-center gap-2">
                <FolderUp className="w-4 h-4" />
                Enviar em Massa
              </TabsTrigger>
            )}</TabsList>

          {/* List Tab */}
          <TabsContent value="list" className="mt-4">
            <Card className="border-slate-200 shadow-none">
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5 text-slate-400" />
                    Desenhos Recentes
                    {totalDrawings > 0 && (
                      <span className="text-sm font-normal text-slate-500">
                        ({totalDrawings} encontrados)
                      </span>
                    )}
                  </CardTitle>
                  
                  {/* Search and Filters */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        ref={searchInputRef}
                        type="text"
                        placeholder="Buscar por código ou arquivo..."
                        value={searchFilter}
                        onChange={(e) => setSearchFilter(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            setPage(0);
                            fetchDrawings();
                          }
                        }}
                        className="pl-9 w-full sm:w-64"
                        data-testid="search-drawings-input"
                      />
                    </div>
                    <select
                      value={typeFilter}
                      onChange={(e) => {
                        setTypeFilter(e.target.value);
                        setPage(0);
                      }}
                      className="px-3 py-2 border border-slate-200 rounded-md text-sm bg-white"
                      data-testid="filter-type-select"
                    >
                      <option value="">Todos os tipos</option>
                      <option value="PDF">PDF</option>
                      <option value="DXF">DXF</option>
                    </select>
                    <Button 
                      onClick={async () => {
                        const currentSearch = searchInputRef.current?.value || searchFilter;
                        setPage(0);
                        setLoading(true);
                        try {
                          const params = new URLSearchParams();
                          params.append('limit', PAGE_SIZE);
                          params.append('skip', '0');
                          if (currentSearch) params.append('search', currentSearch);
                          if (typeFilter) params.append('file_type', typeFilter);
                          
                          const response = await axios.get(`${API_URL}/api/drawings/list?${params.toString()}`);
                          setDrawings(response.data.drawings || []);
                          setTotalDrawings(response.data.total || 0);
                        } catch (error) {
                          console.error('Failed to fetch drawings:', error);
                        } finally {
                          setLoading(false);
                        }
                      }}
                      className="bg-slate-900 hover:bg-slate-800 text-white h-10"
                      data-testid="search-drawings-btn"
                    >
                      Buscar
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-6 space-y-3">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : drawings.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <FileBox className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p>Nenhum desenho cadastrado</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-b border-slate-200 hover:bg-transparent">
                          <TableHead>Código Base</TableHead>
                          <TableHead>Revisões</TableHead>
                          <TableHead>Serviços</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Última Revisão</TableHead>
                          <TableHead>Enviado por</TableHead>
                          <TableHead className="w-[120px]">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {drawings.map((drawing) => (
                          <TableRow key={drawing.id} className={`border-b border-slate-100 ${drawing.searched_revision ? 'bg-blue-50/50' : ''}`}>
                            <TableCell>
                              <div className="flex flex-col gap-1">
                                <code className="text-sm bg-slate-100 px-2 py-1 rounded">
                                  {drawing.display_code || drawing.base_code || drawing.code}
                                </code>
                                {drawing.searched_revision && (
                                  <span className="text-xs text-blue-600 font-medium">
                                    Revisão buscada: {drawing.searched_revision}
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {drawing.revision_count > 1 ? (
                                <Badge className="bg-blue-100 text-blue-700">
                                  {drawing.revision_count} revisões
                                </Badge>
                              ) : (
                                <Badge variant="outline">
                                  Rev. {drawing.display_revision || drawing.latest_revision || '00'}
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {drawing.related_services && drawing.related_services.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {drawing.related_services.map((svc, idx) => (
                                    <Badge 
                                      key={idx} 
                                      variant="outline" 
                                      className={`text-xs cursor-pointer hover:bg-slate-100 ${
                                        drawing.searched_revision && svc.revision?.toUpperCase() === drawing.searched_revision 
                                          ? 'bg-blue-100 border-blue-300 text-blue-700' 
                                          : ''
                                      }`}
                                      title={`${svc.type}${svc.revision?.replace(/^[A-Za-z]+/, '') || '0'} - Clique para baixar`}
                                      onClick={() => {
                                        if (svc.is_current) {
                                          handleDownload(drawing);
                                        } else {
                                          // Find the service drawing and download
                                          const svcDrawing = { ...drawing, id: svc.id, latest_revision: svc.revision };
                                          handleDownload(svcDrawing);
                                        }
                                      }}
                                    >
                                      {svc.type}{svc.revision?.replace(/^[A-Za-z]+/, '') || '0'}
                                    </Badge>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-slate-300 text-sm">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant={drawing.file_type === 'PDF' ? 'default' : 'secondary'}>
                                {drawing.file_type}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-slate-600 text-sm font-medium">
                              {drawing.display_revision || drawing.latest_revision || '00'}
                            </TableCell>
                            <TableCell className="text-slate-500 text-sm">
                              {drawing.uploaded_by_name}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                {drawing.file_type?.toUpperCase() === 'PDF' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handlePreview(drawing)}
                                    className="h-8 w-8 p-0 text-blue-500 hover:text-blue-700"
                                    title="Visualizar PDF"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDownload(drawing)}
                                  className="h-8 w-8 p-0"
                                  title={drawing.revision_count > 1 ? "Ver revisões" : "Baixar"}
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                                {canCreate() && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedDrawing(drawing);
                                      setShowDeleteDialog(true);
                                    }}
                                    className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                                    title="Excluir"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
                
                {/* Pagination */}
                {totalDrawings > PAGE_SIZE && (
                  <div className="flex items-center justify-between p-4 border-t border-slate-200">
                    <p className="text-sm text-slate-500">
                      Mostrando {page * PAGE_SIZE + 1} - {Math.min((page + 1) * PAGE_SIZE, totalDrawings)} de {totalDrawings}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0}
                      >
                        Anterior
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => p + 1)}
                        disabled={(page + 1) * PAGE_SIZE >= totalDrawings}
                      >
                        Próximo
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Batch Download Tab */}
          <TabsContent value="batch-download" className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Instructions & Upload Area */}
              <div className="space-y-4">
                <Alert className="border-blue-200 bg-blue-50">
                  <Info className="w-4 h-4 text-blue-600" />
                  <AlertTitle className="text-blue-800">Como funciona</AlertTitle>
                  <AlertDescription className="text-blue-700">
                    <ol className="list-decimal list-inside mt-2 space-y-1">
                      <li>Prepare uma planilha Excel com a coluna <strong>CODIGO</strong></li>
                      <li>Inclua os códigos completos (ex: 1-010-00958-00)</li>
                      <li>Selecione o tipo de arquivo desejado</li>
                      <li>Envie o arquivo para análise e baixe o ZIP</li>
                    </ol>
                  </AlertDescription>
                </Alert>

                <Card className="border-slate-200 shadow-none">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Enviar Planilha</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* File Type Selector */}
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-2 block">
                        Tipo de arquivo
                      </label>
                      <div className="flex gap-2">
                        <Button
                          variant={batchFileType === 'all' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => handleFileTypeChange('all')}
                          className={batchFileType === 'all' ? 'bg-blue-600' : ''}
                        >
                          Todos
                        </Button>
                        <Button
                          variant={batchFileType === 'pdf' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => handleFileTypeChange('pdf')}
                          className={batchFileType === 'pdf' ? 'bg-red-600' : ''}
                        >
                          Apenas PDF
                        </Button>
                        <Button
                          variant={batchFileType === 'dxf' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => handleFileTypeChange('dxf')}
                          className={batchFileType === 'dxf' ? 'bg-green-600' : ''}
                        >
                          Apenas DXF
                        </Button>
                      </div>
                    </div>

                    {/* Upload Area */}
                    <div
                      className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                        batchFile ? 'border-green-300 bg-green-50' : 'border-slate-300 hover:border-blue-400'
                      }`}
                    >
                      <input
                        type="file"
                        ref={batchFileInputRef}
                        onChange={handleBatchFileSelect}
                        accept=".xlsx,.xls"
                        className="hidden"
                      />
                      
                      {batchFile ? (
                        <div className="space-y-3">
                          <CheckCircle className="w-10 h-10 mx-auto text-green-500" />
                          <p className="font-medium text-green-700">{batchFile.name}</p>
                          <p className="text-sm text-slate-500">
                            Tipo: <strong>{batchFileType === 'all' ? 'Todos' : batchFileType.toUpperCase()}</strong>
                          </p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={clearBatchSelection}
                          >
                            <X className="w-4 h-4 mr-1" />
                            Remover
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <FileUp className="w-10 h-10 mx-auto text-slate-400" />
                          <div>
                            <Button
                              variant="outline"
                              onClick={() => batchFileInputRef.current?.click()}
                              disabled={analyzingBatch}
                            >
                              Selecionar Planilha
                            </Button>
                          </div>
                          <p className="text-sm text-slate-500">
                            Arquivos .xlsx ou .xls
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Analysis Results */}
              <div className="space-y-4">
                <Card className="border-slate-200 shadow-none">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      Análise da Planilha
                      {analyzingBatch && <Loader2 className="w-4 h-4 animate-spin" />}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {!batchAnalysis && !analyzingBatch ? (
                      <div className="text-center py-8 text-slate-400">
                        <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>Envie uma planilha para ver a análise</p>
                      </div>
                    ) : analyzingBatch ? (
                      <div className="text-center py-8">
                        <Loader2 className="w-8 h-8 mx-auto animate-spin text-blue-500" />
                        <p className="mt-2 text-slate-500">Analisando códigos...</p>
                      </div>
                    ) : batchAnalysis ? (
                      <div className="space-y-4">
                        {/* Summary - Clickable filters */}
                        <div className="grid grid-cols-4 gap-3">
                          <div 
                            className={`p-3 rounded-lg text-center cursor-pointer transition-all ${
                              batchStatusFilter === 'all' 
                                ? 'bg-slate-200 ring-2 ring-slate-400' 
                                : 'bg-slate-50 hover:bg-slate-100'
                            }`}
                            onClick={() => setBatchStatusFilter('all')}
                          >
                            <p className="text-2xl font-bold text-slate-600">{batchAnalysis.total}</p>
                            <p className="text-xs text-slate-700">Todos</p>
                          </div>
                          <div 
                            className={`p-3 rounded-lg text-center cursor-pointer transition-all ${
                              batchStatusFilter === 'ok' 
                                ? 'bg-green-200 ring-2 ring-green-400' 
                                : 'bg-green-50 hover:bg-green-100'
                            }`}
                            onClick={() => setBatchStatusFilter('ok')}
                          >
                            <p className="text-2xl font-bold text-green-600">{batchAnalysis.ok}</p>
                            <p className="text-xs text-green-700">Disponíveis</p>
                          </div>
                          <div 
                            className={`p-3 rounded-lg text-center cursor-pointer transition-all ${
                              batchStatusFilter === 'outdated' 
                                ? 'bg-amber-200 ring-2 ring-amber-400' 
                                : 'bg-amber-50 hover:bg-amber-100'
                            }`}
                            onClick={() => setBatchStatusFilter('outdated')}
                          >
                            <p className="text-2xl font-bold text-amber-600">{batchAnalysis.outdated}</p>
                            <p className="text-xs text-amber-700">Revisão Antiga</p>
                          </div>
                          <div 
                            className={`p-3 rounded-lg text-center cursor-pointer transition-all ${
                              batchStatusFilter === 'not_found' 
                                ? 'bg-red-200 ring-2 ring-red-400' 
                                : 'bg-red-50 hover:bg-red-100'
                            }`}
                            onClick={() => setBatchStatusFilter('not_found')}
                          >
                            <p className="text-2xl font-bold text-red-600">{batchAnalysis.not_found}</p>
                            <p className="text-xs text-red-700">Não Encontrados</p>
                          </div>
                        </div>

                        {/* Filter indicator */}
                        {batchStatusFilter !== 'all' && (
                          <div className="flex items-center justify-between bg-slate-100 rounded-lg px-3 py-2">
                            <span className="text-sm text-slate-600">
                              Filtrando por: <strong>{
                                batchStatusFilter === 'ok' ? 'Disponíveis' :
                                batchStatusFilter === 'outdated' ? 'Revisão Antiga' :
                                batchStatusFilter === 'not_found' ? 'Não Encontrados' : 'Todos'
                              }</strong>
                            </span>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => setBatchStatusFilter('all')}
                              className="text-slate-500 hover:text-slate-700"
                            >
                              <X className="w-4 h-4 mr-1" />
                              Limpar filtro
                            </Button>
                          </div>
                        )}

                        {/* Warnings */}
                        {batchAnalysis.outdated > 0 && batchStatusFilter === 'all' && (
                          <Alert className="bg-amber-50 border-amber-200">
                            <AlertTriangle className="w-4 h-4 text-amber-600" />
                            <AlertTitle className="text-amber-800">Aviso de Revisões</AlertTitle>
                            <AlertDescription className="text-amber-700 text-sm">
                              {batchAnalysis.outdated} código(s) solicitado(s) possuem revisões mais recentes disponíveis.
                              O download incluirá a revisão solicitada, mas verifique o relatório.
                            </AlertDescription>
                          </Alert>
                        )}

                        {/* Details */}
                        <div className="border rounded-lg max-h-64 overflow-y-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Código</TableHead>
                                <TableHead>Status</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {batchAnalysis.codes
                                ?.filter(item => {
                                  if (batchStatusFilter === 'all') return true;
                                  if (batchStatusFilter === 'ok') return item.status === 'ok';
                                  if (batchStatusFilter === 'outdated') return item.status === 'outdated';
                                  if (batchStatusFilter === 'not_found') return item.status === 'not_found' || item.status === 'revision_not_found';
                                  return true;
                                })
                                .map((item, idx) => (
                                <TableRow key={idx}>
                                  <TableCell className="font-mono text-sm">
                                    {item.code}
                                  </TableCell>
                                  <TableCell>
                                    {item.status === 'ok' && (
                                      <Badge className="bg-green-100 text-green-700">
                                        <CheckCircle className="w-3 h-3 mr-1" />
                                        OK
                                      </Badge>
                                    )}
                                    {item.status === 'outdated' && (
                                      <Badge className="bg-amber-100 text-amber-700">
                                        <AlertTriangle className="w-3 h-3 mr-1" />
                                        Rev. {item.latest_revision} disponível
                                      </Badge>
                                    )}
                                    {(item.status === 'not_found' || item.status === 'revision_not_found') && (
                                      <Badge className="bg-red-100 text-red-700">
                                        <XCircle className="w-3 h-3 mr-1" />
                                        Não encontrado
                                      </Badge>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>

                        {/* Download Button */}
                        <Button
                          className="w-full bg-blue-600 hover:bg-blue-700"
                          onClick={handleBatchDownloadClick}
                          disabled={downloadingBatch || (batchAnalysis.ok === 0 && batchAnalysis.outdated === 0)}
                        >
                          {downloadingBatch ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Preparando ZIP...
                            </>
                          ) : (
                            <>
                              <Download className="w-4 h-4 mr-2" />
                              Baixar {batchAnalysis.ok + batchAnalysis.outdated} Desenho(s)
                            </>
                          )}
                        </Button>
                      </div>
                    ) : null}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Smart Upload Tab */}
          {canCreate() && (
            <TabsContent value="bulk" className="mt-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Instructions & Upload Area */}
                <div className="space-y-4">
                  <Alert className="border-blue-200 bg-blue-50">
                    <Info className="w-4 h-4 text-blue-600" />
                    <AlertTitle className="text-blue-800">Upload Inteligente</AlertTitle>
                    <AlertDescription className="text-blue-700">
                      <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                        <li>Nome do arquivo = <strong>código completo</strong></li>
                        <li>Ex: <code className="bg-blue-100 px-1 rounded">1-015-00001-02.pdf</code></li>
                        <li>O sistema detecta automaticamente:</li>
                        <ul className="list-disc list-inside ml-4 text-xs">
                          <li>Se é um novo desenho ou nova revisão</li>
                          <li>Se o código existe no catálogo</li>
                        </ul>
                      </ul>
                    </AlertDescription>
                  </Alert>

                  <Card className="border-slate-200 shadow-none">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Upload className="w-5 h-5 text-slate-400" />
                        Selecionar Arquivos
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {/* Drop Zone */}
                      <div
                        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer
                          ${bulkFiles.length > 0 ? 'border-blue-300 bg-blue-50' : 'border-slate-300 hover:border-blue-400 hover:bg-slate-50'}`}
                        onDrop={handleBulkDrop}
                        onDragOver={handleBulkDragOver}
                        onClick={() => bulkFileInputRef.current?.click()}
                        data-testid="bulk-drop-zone"
                      >
                        <input
                          ref={bulkFileInputRef}
                          type="file"
                          multiple
                          accept=".pdf,.dxf"
                          onChange={handleBulkFileSelect}
                          className="hidden"
                        />
                        <FileUp className="w-10 h-10 mx-auto text-slate-400 mb-3" />
                        <p className="text-slate-600 font-medium">
                          {bulkFiles.length > 0 
                            ? `${bulkFiles.length} arquivo(s) selecionado(s)` 
                            : 'Arraste arquivos PDF/DXF aqui'}
                        </p>
                        <p className="text-sm text-slate-500 mt-1">ou clique para selecionar</p>
                      </div>

                      {/* Selected Files */}
                      {bulkFiles.length > 0 && (
                        <div className="mt-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-slate-700">
                              Arquivos selecionados
                            </span>
                            <Button variant="ghost" size="sm" onClick={clearBulkFiles} className="text-red-500 hover:text-red-700 h-7">
                              Limpar todos
                            </Button>
                          </div>
                          <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
                            {bulkFiles.slice(0, 20).map((file, index) => (
                              <div key={index} className="flex items-center justify-between p-2 hover:bg-slate-50">
                                <div className="flex items-center gap-2 min-w-0">
                                  <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                  <span className="text-sm truncate">{file.name}</span>
                                </div>
                                <Button variant="ghost" size="sm" onClick={() => removeBulkFile(index)} className="h-6 w-6 p-0 text-slate-400 hover:text-red-500">
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                            {bulkFiles.length > 20 && (
                              <p className="p-2 text-center text-sm text-slate-500">
                                ... e mais {bulkFiles.length - 20} arquivos
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Analyze Button */}
                      <Button
                        onClick={handleSmartAnalyze}
                        disabled={bulkFiles.length === 0 || bulkUploading}
                        className="w-full mt-4 bg-blue-600 hover:bg-blue-700"
                        data-testid="analyze-btn"
                      >
                        {bulkUploading ? (
                          <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Analisando...</>
                        ) : (
                          <><Search className="w-4 h-4 mr-2" /> Analisar {bulkFiles.length} arquivo(s)</>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                </div>

                {/* Results / Analysis Panel */}
                <Card className="border-slate-200 shadow-none">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileText className="w-5 h-5 text-slate-400" />
                      Análise dos Arquivos
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {bulkResults ? (
                      <div className="space-y-4">
                        {/* Summary */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          <div className="bg-slate-50 rounded-lg p-3 text-center">
                            <p className="text-xl font-bold text-slate-900">{bulkResults.total}</p>
                            <p className="text-xs text-slate-500">Total</p>
                          </div>
                          <div className="bg-green-50 rounded-lg p-3 text-center">
                            <p className="text-xl font-bold text-green-600">{bulkResults.to_link || 0}</p>
                            <p className="text-xs text-green-700">Novos</p>
                          </div>
                          <div className="bg-blue-50 rounded-lg p-3 text-center">
                            <p className="text-xl font-bold text-blue-600">{bulkResults.new_revision || 0}</p>
                            <p className="text-xs text-blue-700">Nova Revisão</p>
                          </div>
                          <div className="bg-amber-50 rounded-lg p-3 text-center">
                            <p className="text-xl font-bold text-amber-600">{bulkResults.not_found || 0}</p>
                            <p className="text-xs text-amber-700">Sem Código</p>
                          </div>
                        </div>

                        {/* Warning for codes not found */}
                        {bulkResults.not_found > 0 && (
                          <Alert className="bg-amber-50 border-amber-200">
                            <AlertTriangle className="w-4 h-4 text-amber-600" />
                            <AlertTitle className="text-amber-800">Códigos não encontrados</AlertTitle>
                            <AlertDescription className="text-amber-700 text-sm">
                              {bulkResults.not_found} arquivo(s) não possuem código correspondente no catálogo.
                              <br/>Marque os que deseja criar automaticamente.
                            </AlertDescription>
                          </Alert>
                        )}

                        {/* Details Table */}
                        <div className="border rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-slate-50">
                                <TableHead className="w-[40px]">
                                  {bulkResults.not_found > 0 && (
                                    <input
                                      type="checkbox"
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setCodesToCreate(bulkResults.details?.not_found?.map(f => f.base_code) || []);
                                        } else {
                                          setCodesToCreate([]);
                                        }
                                      }}
                                      className="rounded"
                                    />
                                  )}
                                </TableHead>
                                <TableHead>Arquivo</TableHead>
                                <TableHead>Status</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {/* New drawings */}
                              {bulkResults.details?.to_link?.map((item, idx) => (
                                <TableRow key={`new-${idx}`} className="bg-green-50/30">
                                  <TableCell>
                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                  </TableCell>
                                  <TableCell className="font-mono text-xs">{item.filename}</TableCell>
                                  <TableCell>
                                    <Badge className="bg-green-100 text-green-700 text-xs">
                                      Novo desenho
                                    </Badge>
                                  </TableCell>
                                </TableRow>
                              ))}
                              
                              {/* New revisions */}
                              {bulkResults.details?.new_revision?.map((item, idx) => (
                                <TableRow key={`rev-${idx}`} className="bg-blue-50/30">
                                  <TableCell>
                                    <RefreshCw className="w-4 h-4 text-blue-500" />
                                  </TableCell>
                                  <TableCell className="font-mono text-xs">{item.filename}</TableCell>
                                  <TableCell>
                                    <Badge className="bg-blue-100 text-blue-700 text-xs">
                                      Rev. {item.revision} (atual: {item.current_latest})
                                    </Badge>
                                  </TableCell>
                                </TableRow>
                              ))}
                              
                              {/* Not found - with checkbox */}
                              {bulkResults.details?.not_found?.map((item, idx) => (
                                <TableRow key={`nf-${idx}`} className="bg-amber-50/30">
                                  <TableCell>
                                    <input
                                      type="checkbox"
                                      checked={codesToCreate.includes(item.base_code)}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setCodesToCreate([...codesToCreate, item.base_code]);
                                        } else {
                                          setCodesToCreate(codesToCreate.filter(c => c !== item.base_code));
                                        }
                                      }}
                                      className="rounded"
                                    />
                                  </TableCell>
                                  <TableCell className="font-mono text-xs">{item.filename}</TableCell>
                                  <TableCell>
                                    <Badge className="bg-amber-100 text-amber-700 text-xs">
                                      Código {item.full_code || item.base_code} não existe
                                    </Badge>
                                  </TableCell>
                                </TableRow>
                              ))}
                              
                              {/* Invalid files */}
                              {bulkResults.details?.invalid?.map((item, idx) => (
                                <TableRow key={`inv-${idx}`} className="bg-red-50/30">
                                  <TableCell>
                                    <XCircle className="w-4 h-4 text-red-500" />
                                  </TableCell>
                                  <TableCell className="font-mono text-xs">{item.filename}</TableCell>
                                  <TableCell>
                                    <Badge className="bg-red-100 text-red-700 text-xs">
                                      {item.reason}
                                    </Badge>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2">
                          <Button
                            onClick={handleSmartUploadConfirm}
                            disabled={uploadingConfirm || (bulkResults.valid === 0 && codesToCreate.length === 0)}
                            className="flex-1 bg-green-600 hover:bg-green-700"
                          >
                            {uploadingConfirm ? (
                              <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Enviando...</>
                            ) : (
                              <><Upload className="w-4 h-4 mr-2" /> 
                                Enviar {bulkResults.valid + (codesToCreate.length > 0 ? codesToCreate.length : 0)} arquivo(s)
                                {codesToCreate.length > 0 && ` (criar ${codesToCreate.length} código(s))`}
                              </>
                            )}
                          </Button>
                          <Button variant="outline" onClick={clearBulkFiles}>
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12 text-slate-400">
                        <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>Selecione arquivos e clique em &quot;Analisar&quot;</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}

          {/* Unlinked Files Tab */}
          <TabsContent value="unlinked" className="mt-4">
            <Card className="border-slate-200 shadow-none">
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Unlink className="w-5 h-5 text-orange-400" />
                    Desenhos Não Vinculados
                    {unlinkedTotal > 0 && (
                      <span className="text-sm font-normal text-slate-500">
                        ({unlinkedTotal} arquivos)
                      </span>
                    )}
                  </CardTitle>
                  
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="text"
                      placeholder="Buscar arquivo..."
                      value={unlinkedSearch}
                      onChange={(e) => {
                        setUnlinkedSearch(e.target.value);
                        setUnlinkedPage(0);
                      }}
                      onKeyDown={(e) => e.key === 'Enter' && fetchUnlinkedFiles()}
                      className="pl-9 w-full sm:w-64"
                    />
                  </div>
                </div>
                <p className="text-sm text-slate-500 mt-2">
                  Arquivos no storage que não estão associados a nenhum código no catálogo.
                </p>
              </CardHeader>
              <CardContent className="p-0">
                {loadingUnlinked ? (
                  <div className="p-6 space-y-3">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : unlinkedFiles.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <CheckCircle className="w-12 h-12 mx-auto text-green-300 mb-3" />
                    <p>Todos os arquivos estão vinculados!</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-b border-slate-200 hover:bg-transparent">
                          <TableHead>Arquivo</TableHead>
                          <TableHead>Código Extraído</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead className="w-[100px]">Status</TableHead>
                          <TableHead className="w-[80px]">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {unlinkedFiles.map((file, idx) => (
                          <TableRow key={idx} className="border-b border-slate-100">
                            <TableCell className="font-medium truncate max-w-[300px]">
                              {file.filename}
                            </TableCell>
                            <TableCell>
                              <code className="text-sm bg-slate-100 px-2 py-1 rounded">
                                {file.code_extracted}
                              </code>
                            </TableCell>
                            <TableCell>
                              <Badge variant={file.file_type === 'PDF' ? 'default' : 'secondary'}>
                                {file.file_type}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className="bg-orange-100 text-orange-700">
                                Não Vinculado
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {file.file_type === 'PDF' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handlePreviewUnlinked(file)}
                                  className="h-8 w-8 p-0 text-blue-500 hover:text-blue-700"
                                  title="Visualizar PDF"
                                  data-testid={`view-unlinked-pdf-${idx}`}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
                
                {/* Pagination for unlinked */}
                {unlinkedTotal > PAGE_SIZE && (
                  <div className="flex items-center justify-between p-4 border-t border-slate-200">
                    <p className="text-sm text-slate-500">
                      Mostrando {unlinkedPage * PAGE_SIZE + 1} - {Math.min((unlinkedPage + 1) * PAGE_SIZE, unlinkedTotal)} de {unlinkedTotal}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setUnlinkedPage(p => Math.max(0, p - 1)); fetchUnlinkedFiles(); }}
                        disabled={unlinkedPage === 0}
                      >
                        Anterior
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setUnlinkedPage(p => p + 1); fetchUnlinkedFiles(); }}
                        disabled={(unlinkedPage + 1) * PAGE_SIZE >= unlinkedTotal}
                      >
                        Próximo
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Upload Dialog (Single) */}
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Novo Desenho</DialogTitle>
              <DialogDescription>
                Envie um arquivo PDF ou DXF para um código específico
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Código do Material</Label>
                <Input
                  placeholder="Buscar código..."
                  value={codeSearch}
                  onChange={(e) => handleCodeSearchChange(e.target.value)}
                  className="mt-1"
                />
                {searchLoading && <p className="text-xs text-slate-500 mt-1">Buscando...</p>}
                {searchResults.length > 0 && (
                  <div className="mt-2 border rounded-lg max-h-40 overflow-y-auto">
                    {searchResults.map((code) => (
                      <div
                        key={code.id}
                        className="p-2 hover:bg-slate-50 cursor-pointer border-b last:border-0"
                        onClick={() => selectCode(code)}
                      >
                        <code className="text-sm">{code.code}</code>
                        <p className="text-xs text-slate-500 truncate">{code.description}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Label>Revisão</Label>
                <Input
                  type="number"
                  value={uploadForm.revision === '' ? '' : uploadForm.revision}
                  onChange={(e) => {
                    const val = e.target.value;
                    setUploadForm({ ...uploadForm, revision: val === '' ? '' : parseInt(val) || '' });
                  }}
                  onBlur={(e) => {
                    if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                      setUploadForm({ ...uploadForm, revision: 0 });
                    }
                  }}
                  className="mt-1"
                  min="0"
                />
              </div>
              <div>
                <Label>Arquivos (PDF/DXF)</Label>
                <Input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.dxf"
                  onChange={handleFileSelect}
                  className="mt-1"
                />
                {uploadForm.files.length > 0 && (
                  <p className="text-xs text-slate-500 mt-1">
                    {uploadForm.files.length} arquivo(s) selecionado(s)
                  </p>
                )}
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Cancelar</Button>
              <Button onClick={handleUpload} disabled={uploading} className="bg-blue-600 hover:bg-blue-700">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Enviar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Excluir Desenho</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja excluir o arquivo &quot;{selectedDrawing?.original_filename}&quot;?
                Esta ação não pode ser desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* PDF Preview Dialog */}
        <Dialog open={showPreviewDialog} onOpenChange={closePreview}>
          <DialogContent className="max-w-6xl h-[90vh] p-0 overflow-hidden">
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <div>
                    <h3 className="font-medium text-slate-900">{previewDrawing?.original_filename}</h3>
                    <p className="text-xs text-slate-500">
                      Código: <code className="bg-slate-200 px-1 rounded">{previewDrawing?.code}</code>
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => previewDrawing && handleDownload(previewDrawing)}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Baixar
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={closePreview}
                    className="h-8 w-8 p-0"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {/* PDF Viewer */}
              <div className="flex-1 bg-slate-200">
                {previewDrawing?.blobUrl && (
                  <iframe
                    src={previewDrawing.blobUrl}
                    className="w-full h-full border-0"
                    title={previewDrawing.original_filename}
                  />
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Revision Selection Dialog */}
        <Dialog open={showRevisionDialog} onOpenChange={setShowRevisionDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Múltiplas Revisões Disponíveis
              </DialogTitle>
              <DialogDescription>
                Este desenho possui {revisionDrawing?.revision_count} revisões. Selecione qual versão deseja baixar.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-2 my-4">
              <Alert className="bg-amber-50 border-amber-200">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertTitle className="text-amber-800">Atenção</AlertTitle>
                <AlertDescription className="text-amber-700">
                  A revisão mais recente é <strong>Rev. {revisionDrawing?.latest_revision}</strong>. 
                  Versões anteriores podem estar desatualizadas.
                </AlertDescription>
              </Alert>
              
              <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                {revisionDrawing?.revisions?.sort((a, b) => b.revision_num - a.revision_num).map((rev) => {
                  const isPdf = (rev.file_type || revisionDrawing.file_type || 'pdf').toUpperCase() === 'PDF';
                  return (
                  <div 
                    key={rev.revision} 
                    className={`p-3 flex items-center justify-between hover:bg-slate-50 ${
                      rev.revision === revisionDrawing.latest_revision ? 'bg-green-50' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Badge 
                        className={rev.revision === revisionDrawing.latest_revision 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-slate-100 text-slate-600'
                        }
                      >
                        Rev. {rev.revision}
                      </Badge>
                      <span className="text-sm text-slate-600 truncate max-w-[180px]">
                        {rev.filename}
                      </span>
                      {rev.revision === revisionDrawing.latest_revision && (
                        <Badge className="bg-green-500 text-white text-xs">
                          Mais recente
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {isPdf && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewRevision(rev)}
                          disabled={previewLoading}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          data-testid={`view-revision-${rev.revision}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Ver
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant={rev.revision === revisionDrawing.latest_revision ? 'default' : 'outline'}
                        onClick={() => handleDownloadRevision(rev)}
                        disabled={downloadingRevision}
                        data-testid={`download-revision-${rev.revision}`}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Baixar
                      </Button>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRevisionDialog(false)}>
                Fechar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Unlinked PDF Preview Dialog */}
        <Dialog open={showUnlinkedPreview} onOpenChange={closeUnlinkedPreview}>
          <DialogContent className="max-w-6xl w-[95vw] h-[90vh] flex flex-col p-0" data-testid="unlinked-pdf-viewer">
            <DialogHeader className="px-4 py-3 border-b border-slate-200">
              <DialogTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                Visualizar PDF (Não Vinculado)
                {unlinkedPreviewFile && (
                  <span className="text-sm font-normal text-slate-500 font-mono bg-slate-100 px-2 py-0.5 rounded">
                    {unlinkedPreviewFile.filename}
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>
            
            <div className="flex-1 overflow-auto bg-slate-100 p-4">
              {unlinkedPreviewLoading ? (
                <div className="flex flex-col items-center justify-center h-full text-slate-500">
                  <Loader2 className="h-10 w-10 animate-spin mb-3 text-blue-500" />
                  <p>Carregando PDF...</p>
                </div>
              ) : unlinkedPreviewUrl ? (
                <div className="flex justify-center">
                  <iframe
                    src={unlinkedPreviewUrl}
                    className="w-full bg-white shadow-lg rounded-lg"
                    style={{ height: '70vh', minWidth: '800px', maxWidth: '100%' }}
                    title="PDF Viewer - Unlinked"
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-red-500">
                  <X className="h-10 w-10 mb-3" />
                  <p>Erro ao carregar PDF</p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Revision Choice Dialog for Batch Download */}
        <Dialog open={showRevisionChoiceDialog} onOpenChange={setShowRevisionChoiceDialog}>
          <DialogContent className="sm:max-w-[500px]" data-testid="revision-choice-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-700">
                <AlertTriangle className="w-5 h-5" />
                Revisões Antigas Detectadas
              </DialogTitle>
              <DialogDescription>
                {batchAnalysis?.outdated} código(s) possuem revisões mais recentes disponíveis.
                Como deseja prosseguir?
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-3 py-4">
              <div
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  batchRevisionChoice === 'requested' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
                onClick={() => setBatchRevisionChoice('requested')}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    batchRevisionChoice === 'requested' ? 'border-blue-500 bg-blue-500' : 'border-slate-300'
                  }`}>
                    {batchRevisionChoice === 'requested' && (
                      <CheckCircle className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Baixar revisões SOLICITADAS</p>
                    <p className="text-sm text-slate-500 mt-1">
                      Baixa exatamente as revisões que estão na planilha, mesmo que sejam antigas.
                    </p>
                  </div>
                </div>
              </div>
              
              <div
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  batchRevisionChoice === 'latest' 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
                onClick={() => setBatchRevisionChoice('latest')}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    batchRevisionChoice === 'latest' ? 'border-green-500 bg-green-500' : 'border-slate-300'
                  }`}>
                    {batchRevisionChoice === 'latest' && (
                      <CheckCircle className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Baixar revisões MAIS RECENTES</p>
                    <p className="text-sm text-slate-500 mt-1">
                      Substitui automaticamente as revisões antigas pelas mais recentes disponíveis.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRevisionChoiceDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={() => handleBatchDownload(batchRevisionChoice)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Baixar Desenhos
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
